import React from 'react'
import { AlertTriangle, MessageCircle, Phone, X } from 'lucide-react'
import {
  SUPPORT_MOBILE,
  buildWhatsAppUrl,
  dismissContractWarn,
  telSupportHref,
} from '../../services/managementService'

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(String(value).slice(0, 10))
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * Soft reminder when contract ends within 10 days. App remains usable.
 */
export default function ContractExpiringWarning({ portal, onClose }) {
  if (!portal) return null

  const days = Number(portal.DaysToContractExpiry ?? 0)
  const nearest = (portal.Contracts || [])
    .filter((c) => !c.IsExpired && String(c.Status || '').toLowerCase() === 'active')
    .sort((a, b) => String(a.EndDate).localeCompare(String(b.EndDate)))[0]

  const endDate = portal.NearestContractExpiry || nearest?.EndDate

  const waUrl = buildWhatsAppUrl({
    customerName: portal.CustomerName,
    registerCode: portal.Code,
    reason: 'Our software contract is expiring soon and we would like to renew.',
    details: nearest
      ? `Contract: ${nearest.ContractNo || '—'}; Ends: ${formatDate(endDate)}; Days left: ${days}`
      : `Ends: ${formatDate(endDate)}; Days left: ${days}`,
  })

  const handleClose = () => {
    dismissContractWarn()
    onClose?.()
  }

  const dayLabel =
    days <= 0 ? 'today' : days === 1 ? 'in 1 day' : `in ${days} days`

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50">
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-amber-200 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="contract-expiring-title"
      >
        <div className="bg-amber-50 px-5 py-4 border-b border-amber-100 flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h2 id="contract-expiring-title" className="text-lg font-bold text-amber-950">
              Contract expiring soon
            </h2>
            <p className="text-sm text-amber-900/80 mt-1">
              Your contract ends {dayLabel}. Please renew before the end date to avoid losing access.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-amber-800 hover:bg-amber-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm">
          {nearest || endDate ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-slate-500 text-xs uppercase font-semibold tracking-wide">
                Contract
              </p>
              <p className="font-semibold text-slate-900 mt-1">
                {nearest?.ContractNo || 'Active contract'}
                {nearest?.ProductName ? ` · ${nearest.ProductName}` : ''}
              </p>
              <p className="text-slate-600 mt-1">
                Ends on {formatDate(endDate)}
                {days >= 0 ? ` · ${days} day${days === 1 ? '' : 's'} left` : ''}
              </p>
            </div>
          ) : (
            <p className="text-slate-600">Your contract is approaching its end date.</p>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={waUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:brightness-95"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <a
              href={telSupportHref()}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
            >
              <Phone className="w-4 h-4" />
              Call {SUPPORT_MOBILE}
            </a>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 ml-auto"
            >
              Remind me later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
